import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Account, TenantMembership, UserRole } from '@atomfit/shared';
import { useBookingStore } from './booking.store.js';
import { useMembershipCheckoutStore } from './membership-checkout.store.js';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  tenantSlug: string | null;
  role: UserRole | null;
  availableTenants: TenantMembership[];
  account: Account | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccount: (account: Account) => void;
  setRole: (role: UserRole | null) => void;
  setTenantSlug: (slug: string | null) => void;
  setAvailableTenants: (tenants: TenantMembership[]) => void;
  logout: () => void;
}

function clearAccountPaymentState() {
  useBookingStore.getState().clearPaymentSession();
  useMembershipCheckoutStore.getState().resetPayment();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      tenantSlug: null,
      role: null,
      availableTenants: [],
      account: null,
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setAccount: (account) => {
        if (get().account?.id !== account.id) clearAccountPaymentState();
        set({ account });
      },
      setRole: (role) => set({ role }),
      setTenantSlug: (tenantSlug) => set({ tenantSlug }),
      setAvailableTenants: (availableTenants) => set({ availableTenants }),
      logout: () => {
        clearAccountPaymentState();
        set({ accessToken: null, refreshToken: null, tenantSlug: null, role: null, account: null, availableTenants: [] });
      },
    }),
    {
      name: 'atomfit-auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        tenantSlug: s.tenantSlug,
        role: s.role,
        availableTenants: s.availableTenants,
        account: s.account,
      }),
    },
  ),
);
